import React, { useImperativeHandle, forwardRef } from 'react';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { useDataAuth } from '../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel  from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import MenuItem  from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid  from '@mui/material/Grid';
import Box from '@mui/material/Box';

import { MultipleSelect } from '../others/MultipleSelect';
import identificationTypes from "../../data/identificationTypes.json";
import taxResponsibilities from "../../data/taxResponsibilities.json";
import { getCompany } from '../../services/sharedControls.service';
import { calculateDV } from '../../utils/invoice';

import '../../styles/components/_forms.scss'


export const ClientForm = forwardRef(({ dto, handleInputChange, handleDirectInputChange }, ref) => {
    const fieldsRequired = ['receiver_id', 'receiver', 'receiver_tax_id', 'receiver_tax_scheme_id', 'receiver_tax_payer'];
    const [errorInputs, , , validForm] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();

    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    const handleSelectObligation = (event) => {
        const { target: { value }, } = event;
        handleDirectInputChange('receiver_tax_level_code', typeof value === 'string' ? value.split(',') : value);
    };

    const setClientData = (values) => {
        const oblig = values.supplier_tax_level_code?.split(",") || [];
        handleDirectInputChange('receiver', values.business_name);
        handleDirectInputChange('receiver_id', values.identification);
        handleDirectInputChange('receiver_digit', values.type_identification === '31' ? values.identification_digit: '');
        handleDirectInputChange('receiver_tax_id', values.type_identification);
        handleDirectInputChange('receiver_tax_payer', values.supplier_tax_payer);
        handleDirectInputChange('receiver_tax_scheme_id', values.supplier_tax_scheme);
        handleDirectInputChange('receiver_tax_level_code', oblig);
        handleDirectInputChange('receiver_country', values.supplier_country);
        handleDirectInputChange('receiver_state', values.supplier_state);
        handleDirectInputChange('receiver_city', values.supplier_city);
        handleDirectInputChange('receiver_postalCode', values.supplier_postal_code);
        handleDirectInputChange('receiver_phone', values.supplier_phone);
        handleDirectInputChange('reciever_extension', values.supplier_extension);
        handleDirectInputChange('receiver_address', values.supplier_address);
        handleDirectInputChange('receiver_email', values.supplier_email);
        
    }
    const handleClient = async ({ target }) => {
        const identification = target.value;
        handleDirectInputChange("receiver_id", identification);
        if (identification.length > 7) {
            setTimeout(async () => {
                const response = await getCompany(userSession.token, identification);
                if (response?.data) {
                    setClientData(response.data[0]);
                }else{
                    calculate()
                }
            }, 1000);
        }
    };

    const calculate = () => {
        if(!dto.receiver_digit && dto.receiver_digit === ''){
            if(dto.receiver_tax_id === '31'){ 
                const dv = calculateDV(dto.receiver_id);
                handleDirectInputChange("receiver_digit", dv);
            }else{
                handleDirectInputChange("receiver_digit", "");
            }
        }
    }
    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth required size="small" type='number' label={t('commonFields.idNumber')} name="receiver_id" className='input-no-spinner'
                        value={dto.receiver_id}
                        onChange={handleClient}
                        onBlur={calculate}
                        error={Boolean(errorInputs.receiver_id?.isError)}
                        helperText={errorInputs.receiver_id?.messageError || ''}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth disabled={dto.receiver_tax_id === '31'} size="small" type='number' label={t('commonFields.verificationDigit')} name="receiver_digit" className='input-no-spinner'
                        value={dto.receiver_digit}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.receiver_tax_id?.isError)}
                    >
                        <InputLabel>{t('commonFields.identificationType')}</InputLabel>
                        <Select label={t('commonFields.identificationType')} name="receiver_tax_id"
                            value={dto.receiver_tax_id}
                            onChange={handleInputChange}
                            onBlur={calculate}
                            MenuProps={{
                                PaperProps: {
                                    style: { maxHeight: 200, overflowY: "auto", },
                                },
                            }}
                        >
                            {identificationTypes.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                        </Select>
                        {errorInputs.receiver_tax_id?.isError && <FormHelperText>{errorInputs.receiver_tax_id.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.receiver_tax_payer?.isError)}
                    >
                        <InputLabel>{t('commonFields.taxPayer')}</InputLabel>
                        <Select label={t('commonFields.taxPayer')} name="receiver_tax_payer"
                            value={dto.receiver_tax_payer}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="1">{t('commonFields.legal')}</MenuItem>
                            <MenuItem value="2">{t('commonFields.natural')}</MenuItem>
                        </Select>
                        {errorInputs.receiver_tax_payer?.isError && <FormHelperText>{errorInputs.receiver_tax_payer.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth required size="small" label={t('commonFields.businessName')} name="receiver"
                        value={dto.receiver}
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.receiver?.isError)}
                        helperText={errorInputs.receiver?.messageError || ''}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.receiver_tax_scheme_id?.isError)}
                    >
                        <InputLabel>{t('commonFields.taxType')}</InputLabel>
                        <Select  label={t('commonFields.taxType')} name="receiver_tax_scheme_id"
                            value={dto.receiver_tax_scheme_id}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="01">IVA</MenuItem>
                            <MenuItem value="04">INC</MenuItem>
                            <MenuItem value="ZA">IVA e INC</MenuItem>
                            <MenuItem value="ZZ">{t('commonFields.notApplicable')}</MenuItem>
                        </Select>
                        {errorInputs.receiver_tax_scheme_id?.isError && <FormHelperText>{errorInputs.receiver_tax_scheme_id.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <MultipleSelect
                        data={taxResponsibilities}
                        values={dto.receiver_tax_level_code}
                        handleChange={handleSelectObligation}
                        label={t('commonFields.obligations')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
});
